import { useState } from "react";
import { useNavigate } from "react-router";
import SVGBiking from "../../assets/biking.svg";
import Button from "../../components/Button";
import ButtonGroup from "../../components/ButtonGroup";
import Form from "../../components/Form";
import { FieldInfo } from "../../components/FormField";
import Header from "../../components/Header";
import Input from "../../components/Input";
import Panel from "../../components/Panel";
import ToggleSwitch from "../../components/ToggleSwitch";
import { useBikeMutation } from "../../hooks/useBikes";
import { SuspensionType } from "../../http/onlyclicksApi";
import { translate } from "../../i18n";
import { Language } from "../../types";
import styles from "./NewBikePage.module.css";

interface FormData {
  bike: {
    name: string;
  };
  fork: {
    type: SuspensionType;
    show_sag: boolean;
    show_lsc: boolean;
    show_hsc: boolean;
    show_lsr: boolean;
    show_hsr: boolean;
    show_volume_spacers: boolean;
    show_negative_psi: boolean;
  };
  shock: {
    type: SuspensionType;
    show_sag: boolean;
    show_lsc: boolean;
    show_hsc: boolean;
    show_lsr: boolean;
    show_hsr: boolean;
    show_volume_spacers: boolean;
  };
}

export enum Step {
  Bike = 0,
  Fork,
  Shock,
  Wheels,
}

const defaultFormData: FormData = {
  bike: {
    name: "",
  },
  fork: {
    type: "air",
    show_sag: false,
    show_lsc: false,
    show_hsc: false,
    show_lsr: false,
    show_hsr: false,
    show_volume_spacers: false,
    show_negative_psi: false,
  },
  shock: {
    type: "coil",
    show_sag: false,
    show_lsc: false,
    show_hsc: false,
    show_lsr: false,
    show_hsr: false,
    show_volume_spacers: false,
  },
};

const NewBikePage = () => {
  const steps = [Step.Fork, Step.Shock, Step.Bike];
  const [step, setStep] = useState<Step>(Step.Fork);
  const [formData, setFormData] = useState<FormData>(defaultFormData);

  const isFirstStep = steps.indexOf(step) === 0;
  const isLastStep = steps.indexOf(step) === steps.length - 1;
  const nextStep = () => {
    if (!isLastStep) setStep(steps[steps.indexOf(step) + 1]!);
  };
  const prevStep = () => setStep(steps[steps.indexOf(step) - 1] ?? step);

  const mutation = useBikeMutation();
  const i18n = translate(Language.English);

  return (
    <div className={styles.layout}>
      <Header backLink="/garage" heading="Add a bike" />
      {!mutation.isIdle && !mutation.isPending && (
        <SuccessDisplay
          isSuccess={mutation.isSuccess}
          isError={mutation.isError}
          errorMessage={mutation.error?.message}
        />
      )}
      {(mutation.isIdle || mutation.isPending) && (
        <Form<FormData>
          className={styles.form}
          defaultValues={formData}
          onSubmit={async ({ value }) => {
            switch (step) {
              case Step.Bike:
                setFormData({
                  ...formData,
                  bike: value.bike,
                });
                break;
              case Step.Fork:
                setFormData({
                  ...formData,
                  fork: value.fork,
                });
                break;
              case Step.Shock:
                setFormData({
                  ...formData,
                  shock: value.shock,
                });
                break;
            }

            if (!isLastStep) {
              nextStep();
            } else {
              await mutation.mutateAsync({
                name: value.bike.name,
                show_wheels: true,
                fork:
                  JSON.stringify(value.fork) ===
                  JSON.stringify(defaultFormData.fork)
                    ? undefined
                    : value.fork,
                shock:
                  JSON.stringify(value.shock) ===
                  JSON.stringify(defaultFormData.shock)
                    ? undefined
                    : value.shock,
              });
            }
          }}
        >
          {(form) => (
            <>
              <div className={styles.formContent}>
                <Panel title={i18n.stepTitle(step)}>
                  {step === Step.Fork && (
                    <>
                      <div className={styles.field}>
                        <form.Field name="fork.type">
                          {(field) => (
                            <>
                              <label className={styles.label}>
                                Suspension Type
                              </label>
                              <ButtonGroup>
                                <ButtonGroup.Button
                                  isActive={field.state.value === "air"}
                                  onClick={() => field.handleChange("air")}
                                >
                                  Air
                                </ButtonGroup.Button>
                                <ButtonGroup.Button
                                  isActive={field.state.value === "coil"}
                                  onClick={() => field.handleChange("coil")}
                                >
                                  Coil
                                </ButtonGroup.Button>
                              </ButtonGroup>
                            </>
                          )}
                        </form.Field>
                      </div>
                      <div className={styles.field}>
                        <form.Field name="fork.show_sag">
                          {(field) => (
                            <ToggleSwitch
                              fullWidth={true}
                              name={field.name}
                              label={i18n.sag}
                              checked={field.state.value}
                              onChange={(e) => field.handleChange(e)}
                            />
                          )}
                        </form.Field>
                      </div>
                      <div className={styles.field}>
                        <form.Field name="fork.show_lsc">
                          {(field) => (
                            <ToggleSwitch
                              fullWidth={true}
                              name={field.name}
                              label={i18n.lsc(false)}
                              checked={field.state.value}
                              onChange={(e) => field.handleChange(e)}
                            />
                          )}
                        </form.Field>
                      </div>
                      <div className={styles.field}>
                        <form.Field name="fork.show_hsc">
                          {(field) => (
                            <ToggleSwitch
                              fullWidth={true}
                              name={field.name}
                              label={i18n.hsc(false)}
                              checked={field.state.value}
                              onChange={(e) => field.handleChange(e)}
                            />
                          )}
                        </form.Field>
                      </div>
                      <div className={styles.field}>
                        <form.Field name="fork.show_lsr">
                          {(field) => (
                            <ToggleSwitch
                              fullWidth={true}
                              name={field.name}
                              label={i18n.lsr(true, false)}
                              checked={field.state.value}
                              onChange={(e) => field.handleChange(e)}
                            />
                          )}
                        </form.Field>
                      </div>
                      <div className={styles.field}>
                        <form.Field name="fork.show_hsr">
                          {(field) => (
                            <ToggleSwitch
                              fullWidth={true}
                              name={field.name}
                              label={i18n.hsr(false)}
                              checked={field.state.value}
                              onChange={(e) => field.handleChange(e)}
                            />
                          )}
                        </form.Field>
                      </div>
                      <form.Subscribe
                        selector={(state) => state.values.fork?.type}
                      >
                        {(type) => (
                          <>
                            {type === "air" && (
                              <>
                                <div className={styles.field}>
                                  <form.Field name="fork.show_volume_spacers">
                                    {(field) => (
                                      <ToggleSwitch
                                        fullWidth={true}
                                        name={field.name}
                                        label={i18n.volume_spacers}
                                        checked={field.state.value}
                                        onChange={(e) => field.handleChange(e)}
                                      />
                                    )}
                                  </form.Field>
                                </div>
                                <div className={styles.field}>
                                  <form.Field name="fork.show_negative_psi">
                                    {(field) => (
                                      <ToggleSwitch
                                        fullWidth={true}
                                        name={field.name}
                                        label={i18n.negative_air_pressure(
                                          false,
                                        )}
                                        checked={field.state.value}
                                        onChange={(e) => field.handleChange(e)}
                                      />
                                    )}
                                  </form.Field>
                                </div>
                              </>
                            )}
                          </>
                        )}
                      </form.Subscribe>
                    </>
                  )}
                  {step === Step.Shock && (
                    <>
                      <div className={styles.field}>
                        <form.Field name="shock.type">
                          {(field) => (
                            <>
                              <label className={styles.label}>
                                Suspension Type
                              </label>
                              <ButtonGroup>
                                <ButtonGroup.Button
                                  isActive={field.state.value === "air"}
                                  onClick={() => field.handleChange("air")}
                                >
                                  Air
                                </ButtonGroup.Button>
                                <ButtonGroup.Button
                                  isActive={field.state.value === "coil"}
                                  onClick={() => field.handleChange("coil")}
                                >
                                  Coil
                                </ButtonGroup.Button>
                              </ButtonGroup>
                            </>
                          )}
                        </form.Field>
                      </div>
                      <div className={styles.field}>
                        <form.Field name="shock.show_sag">
                          {(field) => (
                            <ToggleSwitch
                              fullWidth={true}
                              name={field.name}
                              label={i18n.sag}
                              checked={field.state.value}
                              onChange={(e) => field.handleChange(e)}
                            />
                          )}
                        </form.Field>
                      </div>
                      <div className={styles.field}>
                        <form.Field name="shock.show_lsc">
                          {(field) => (
                            <ToggleSwitch
                              fullWidth={true}
                              name={field.name}
                              label={i18n.lsc(false)}
                              checked={field.state.value}
                              onChange={(e) => field.handleChange(e)}
                            />
                          )}
                        </form.Field>
                      </div>
                      <div className={styles.field}>
                        <form.Field name="shock.show_hsc">
                          {(field) => (
                            <ToggleSwitch
                              fullWidth={true}
                              name={field.name}
                              label={i18n.hsc(false)}
                              checked={field.state.value}
                              onChange={(e) => field.handleChange(e)}
                            />
                          )}
                        </form.Field>
                      </div>
                      <div className={styles.field}>
                        <form.Field name="shock.show_lsr">
                          {(field) => (
                            <ToggleSwitch
                              fullWidth={true}
                              name={field.name}
                              label={i18n.lsr(true, false)}
                              checked={field.state.value}
                              onChange={(e) => field.handleChange(e)}
                            />
                          )}
                        </form.Field>
                      </div>
                      <div className={styles.field}>
                        <form.Field name="shock.show_hsr">
                          {(field) => (
                            <ToggleSwitch
                              fullWidth={true}
                              name={field.name}
                              label={i18n.hsr(false)}
                              checked={field.state.value}
                              onChange={(e) => field.handleChange(e)}
                            />
                          )}
                        </form.Field>
                      </div>
                      <form.Subscribe
                        selector={(state) => state.values.shock?.type}
                      >
                        {(type) => (
                          <>
                            {type === "air" && (
                              <div className={styles.field}>
                                <form.Field name="shock.show_volume_spacers">
                                  {(field) => (
                                    <ToggleSwitch
                                      fullWidth={true}
                                      name={field.name}
                                      label={i18n.volume_spacers}
                                      checked={field.state.value}
                                      onChange={(e) => field.handleChange(e)}
                                    />
                                  )}
                                </form.Field>
                              </div>
                            )}
                          </>
                        )}
                      </form.Subscribe>
                    </>
                  )}
                  {step === Step.Bike && (
                    <form.Field
                      name="bike.name"
                      validators={{
                        onChange: ({ value }) =>
                          !value ? "A name is required" : undefined,
                      }}
                    >
                      {(field) => (
                        <>
                          <label htmlFor={field.name}>{i18n.name}</label>
                          <Input
                            name={field.name}
                            value={field.state.value}
                            onBlur={field.handleBlur}
                            onChange={(e) => field.handleChange(e.target.value)}
                          />
                          <FieldInfo field={field} />
                        </>
                      )}
                    </form.Field>
                  )}
                </Panel>
              </div>
              <form.Subscribe
                selector={(state) => [state.canSubmit, state.isSubmitting]}
              >
                {([canSubmit, isSubmitting]) => (
                  <div className={styles.submitBar}>
                    <Button
                      type="accent_secondary"
                      fullWidth={true}
                      isLoading={mutation.isPending}
                      buttonProps={{
                        disabled: !canSubmit || isSubmitting || isFirstStep,
                        onClick: () => {
                          if (!isFirstStep) {
                            prevStep();
                          }
                        },
                      }}
                    >
                      Back
                    </Button>
                    <Button
                      type="accent"
                      fullWidth={true}
                      isLoading={isSubmitting}
                      buttonProps={{
                        type: "submit",
                        disabled: !canSubmit || isSubmitting,
                      }}
                    >
                      {!isLastStep ? "Continue" : "Submit"}
                    </Button>
                  </div>
                )}
              </form.Subscribe>
            </>
          )}
        </Form>
      )}
    </div>
  );
};

interface SuccessDisplayProps {
  isSuccess: boolean;
  isError: boolean;
  errorMessage?: string;
}

const SuccessDisplay = ({
  isSuccess,
  isError,
  errorMessage,
}: SuccessDisplayProps) => {
  const navigate = useNavigate();

  return (
    <div className={styles.display}>
      {isError && (
        <>
          An error occurred: {errorMessage}
          <Button type="secondary">Retry</Button>
        </>
      )}
      {isSuccess && (
        <>
          <div className={styles.illustratedMessage}>
            <h3>You're all set.</h3>
            <img src={SVGBiking} className={styles.illustration} />
          </div>
          <Button
            type="accent"
            fullWidth={true}
            buttonProps={{
              onClick: () => {
                navigate("/setups");
              },
            }}
          >
            Continue
          </Button>
        </>
      )}
    </div>
  );
};

export default NewBikePage;
